import React, { useEffect, useState } from 'react';
import { Printer, Download, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button, Card, Input, Modal, Th, Td, EmptyState, StatCard } from './ui';
import { fmtMoney, fmtNum, fmtDate } from '../lib/utils';
import PaymentsLedgerPrint from './PaymentsLedgerPrint';
import { downloadElementAsPdf } from '../lib/pdf';

const MODES = [
  ['date', 'Date-wise'],
  ['week', 'Week-wise'],
  ['month', 'Month-wise'],
  ['year', 'Year-wise'],
  ['customer', 'Customer-wise'],
];

function weekStart(dstr) {
  const dt = new Date(dstr);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  return dt.toISOString().slice(0, 10);
}

export function bookAmt(v) {
  const n = Number(v || 0);
  return n % 1 ? n.toFixed(3) : n.toLocaleString('en-US');
}

export function bookDate(d) {
  const dt = new Date(d);
  return `${dt.getDate()}/${dt.getMonth() + 1}/${String(dt.getFullYear()).slice(2)}`;
}

function keyLabel(mode, key) {
  if (mode === 'date') return fmtDate(key);
  if (mode === 'week') {
    const end = new Date(key);
    end.setDate(end.getDate() + 6);
    return `${fmtDate(key)} – ${fmtDate(end.toISOString().slice(0, 10))}`;
  }
  if (mode === 'month') return new Date(`${key}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  return key;
}

export default function PaymentsLedger() {
  const today = new Date().toISOString().slice(0, 10);
  const [entries, setEntries] = useState([]);
  const [company, setCompany] = useState(null);
  const [mode, setMode] = useState('date');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [printOpen, setPrintOpen] = useState(false);
  const [view, setView] = useState('book');

  useEffect(() => {
    api.get('/payments/ledger').then((r) => setEntries(r.data.entries));
    api.get('/settings').then((r) => setCompany(r.data?.company)).catch(() => {});
  }, []);

  const filtered = entries.filter((e) => (!from || e.date >= from) && (!to || e.date <= to));
  const collectors = [...new Set(filtered.map((e) => e.collectedBy))].sort();

  const map = {};
  for (const e of filtered) {
    const key = mode === 'date' ? e.date
      : mode === 'week' ? weekStart(e.date)
      : mode === 'month' ? (e.date || '').slice(0, 7)
      : mode === 'year' ? (e.date || '').slice(0, 4)
      : e.customerName;
    if (!map[key]) map[key] = { key, label: keyLabel(mode, key), total: 0, count: 0, collectors: {} };
    map[key].total += Number(e.amount || 0);
    map[key].count += 1;
    map[key].collectors[e.collectedBy] = (map[key].collectors[e.collectedBy] || 0) + Number(e.amount || 0);
  }
  const rows = Object.values(map).sort((a, b) => (a.key < b.key ? 1 : -1));
  const totals = {};
  for (const c of collectors) totals[c] = rows.reduce((a, r) => a + (r.collectors[c] || 0), 0);
  const grandTotal = rows.reduce((a, r) => a + r.total, 0);

  const blockMap = {};
  for (const e of filtered) {
    const d = e.date || '—';
    if (!blockMap[d]) blockMap[d] = { date: d, entries: [], total: 0 };
    blockMap[d].entries.push(e);
    blockMap[d].total += Number(e.amount || 0);
  }
  const blocks = Object.values(blockMap).sort((a, b) => (a.date > b.date ? 1 : -1));

  const periodLabel = from && to && from === to && from === today ? `Today · ${fmtDate(today)}`
    : from || to ? `${from ? fmtDate(from) : '…'} → ${to ? fmtDate(to) : '…'}` : 'All Time';
  const groupLabel = MODES.find(([k]) => k === mode)[1];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard label="Total Collected" value={fmtMoney(grandTotal)} accent="text-emerald-600" />
        <StatCard label="Payments" value={fmtNum(filtered.length)} />
        <StatCard label="Collectors" value={fmtNum(collectors.length)} />
        <StatCard label="Period" value={periodLabel} />
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          {MODES.map(([k, label]) => (
            <Button key={k} size="sm" variant={mode === k ? 'primary' : 'outline'} onClick={() => setMode(k)} data-testid={`ledger-mode-${k}`}>{label}</Button>
          ))}
          <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ledger-from" />
          <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ledger-to" />
          <Button size="sm" variant={from === today && to === today ? 'primary' : 'outline'} onClick={() => { setFrom(today); setTo(today); }} data-testid="ledger-today-btn">Today</Button>
          <Button size="sm" variant="outline" onClick={() => { setFrom(`${today.slice(0, 7)}-01`); setTo(today); }} data-testid="ledger-thismonth-btn">This Month</Button>
          <Button size="sm" variant={!from && !to ? 'primary' : 'outline'} onClick={() => { setFrom(''); setTo(''); }} data-testid="ledger-clear-btn">All Time</Button>
          <div className="flex-1" />
          {mode === 'date' && (
            <div className="flex gap-1 bg-slate-100 p-1 rounded-md">
              {[['book', 'Book View'], ['table', 'Table']].map(([k, label]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`ledger-view-${k}`}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${view === k ? 'bg-white text-[#1E3A8A] shadow-sm' : 'text-slate-500'}`}>{label}</button>
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" onClick={() => setPrintOpen(true)} data-testid="ledger-print-btn"><Printer size={15} /> Print / PDF</Button>
        </div>
      </Card>

      {mode === 'date' && view === 'book' ? (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-heading font-bold text-slate-700">
            Collection Book · Date-wise <span className="font-normal text-slate-400">({periodLabel})</span>
          </div>
          {blocks.length ? (
            <div className="p-5" style={{ columnWidth: 220, columnGap: '2rem' }}>
              {blocks.map((b) => (
                <div key={b.date} className="mb-5" style={{ breakInside: 'avoid' }} data-testid="book-block">
                  <p className="text-center font-heading font-bold text-slate-800 border-b-2 border-slate-700 w-fit mx-auto px-3 pb-0.5 mb-2 num">{bookDate(b.date)}</p>
                  {b.entries.map((e, i) => (
                    <div key={i} className="flex items-baseline gap-2 py-0.5 border-b border-slate-100" data-testid="book-entry">
                      <span className="num font-semibold text-slate-800 w-20 text-right">{bookAmt(e.amount)}</span>
                      <span className="text-slate-400">—</span>
                      <span className="text-sm text-slate-600 truncate">{e.collectedBy}</span>
                    </div>
                  ))}
                  <div className="flex items-baseline gap-2 pt-1 mt-0.5 border-t-2 border-slate-700">
                    <span className="num font-bold text-slate-900 w-20 text-right" style={{ borderBottom: '3px double #334155' }}>{bookAmt(b.total)}</span>
                    <span className="text-xs text-slate-400">total</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <EmptyState title="No payments collected yet" icon={Wallet} />}
          {blocks.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 text-sm">
              <span className="text-slate-500">Grand Total:</span>
              <span className="num font-bold text-emerald-600">{fmtMoney(grandTotal)}</span>
            </div>
          )}
        </Card>
      ) : (
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-heading font-bold text-slate-700">
          Amount Collected · {groupLabel} <span className="font-normal text-slate-400">({periodLabel})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>{mode === 'customer' ? 'Customer' : 'Period'}</Th>
                {collectors.map((c) => <Th key={c} right>{c}</Th>)}
                <Th right>Payments</Th><Th right>Total</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.key} data-testid="payments-ledger-row" className="hover:bg-slate-50/60 transition-colors">
                  <Td className="font-medium text-slate-800">{r.label}</Td>
                  {collectors.map((c) => (
                    <Td key={c} right className={`num ${r.collectors[c] ? '' : 'text-slate-300'}`}>{r.collectors[c] ? fmtMoney(r.collectors[c]) : '—'}</Td>
                  ))}
                  <Td right className="num">{fmtNum(r.count)}</Td>
                  <Td right className="num font-semibold">{fmtMoney(r.total)}</Td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <Td className="font-bold text-slate-800">Total</Td>
                  {collectors.map((c) => <Td key={c} right className="num font-bold">{fmtMoney(totals[c])}</Td>)}
                  <Td right className="num font-bold">{fmtNum(filtered.length)}</Td>
                  <Td right className="num font-bold text-emerald-600">{fmtMoney(grandTotal)}</Td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {!rows.length && <EmptyState title="No payments collected yet" icon={Wallet} />}
      </Card>
      )}

      {printOpen && (
        <LedgerPrintModal groupLabel={groupLabel} periodLabel={periodLabel} rows={rows} collectors={collectors}
          totals={totals} grandTotal={grandTotal} company={company} mode={mode} from={from} to={to}
          blocks={mode === 'date' && view === 'book' ? blocks : null} onClose={() => setPrintOpen(false)} />
      )}
    </div>
  );
}

function LedgerPrintModal({ groupLabel, periodLabel, rows, collectors, totals, grandTotal, company, mode, from, to, blocks, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const suffix = from || to ? `${from || 'start'}_to_${to || 'now'}` : 'All-Time';
      await downloadElementAsPdf(document.getElementById('print-area'), `Payments-Ledger-${mode}-${suffix}.pdf`, blocks ? 'portrait' : 'landscape');
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };
  return (
    <Modal open size="lg" title={`Payments Ledger · ${blocks ? 'Collection Book' : groupLabel}`} onClose={onClose}
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="ledger-window-print-btn"><Printer size={16} /> Print</Button>
        <Button onClick={download} disabled={downloading} data-testid="ledger-download-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div id="print-area">
        <PaymentsLedgerPrint groupLabel={groupLabel} periodLabel={periodLabel} rows={rows} collectors={collectors}
          totals={totals} grandTotal={grandTotal} company={company} blocks={blocks} />
      </div>
    </Modal>
  );
}
